import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Workflow from 'models/Workflow';
import {
  completeWorkflowAction,
  invokeWorkflowAction,
  showWorkflow,
  WorkflowActionIntent,
  workflowQueryKeys,
  WorkflowValuesInput,
  writeWorkflowValues,
} from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';

// A write's response (create / value-write / complete / rerun / dismiss) now
// reflects the FINAL settled state — the evaluator runs synchronously, including
// re-run cascades (P1-1) — so the acting user's own change renders straight from
// the response. The interval below is only background polling, to pick up changes
// made by other users (no WebSockets until Phase 2).
const POLL_INTERVAL_MS = 2000;

/**
 * The single data hook for a workflow run (the key WEB-0 architectural ask).
 *
 * All fetching lives here so the rest of the UI is agnostic to *how* fresh data
 * arrives. Today it polls `GET /api/workflows/:id` until the run is terminal;
 * swapping to a WebSocket subscription in Phase 2 is a one-file change — only the
 * `useQuery` block below changes, the returned shape stays the same.
 */
export default function useWorkflow(id: string | undefined) {
  const queryClient = useQueryClient();
  const { addToast } = useToastActions();

  const query = useQuery({
    queryKey: workflowQueryKeys.show(id ?? ''),
    queryFn: ({ signal }) => showWorkflow(id!, signal),
    enabled: !!id,
    // --- swap point: replace this background polling with a subscription in Phase 2 ---
    // Background-only: the acting user's writes already return settled state; this
    // catches other users' changes while the run is live, stopping once terminal.
    refetchInterval: (q) => (q.state.data && q.state.data.isTerminal ? false : POLL_INTERVAL_MS),
    refetchIntervalInBackground: true,
  });

  const onWriteSuccess = (workflow: Workflow) => {
    if (id) queryClient.setQueryData(workflowQueryKeys.show(id), workflow);
  };

  const onWriteError = () => addToast({ text: 'Something went wrong. Please try again' });

  const valuesMutation = useMutation({
    mutationFn: (values: WorkflowValuesInput) => writeWorkflowValues({ workflowId: id!, values }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const actionMutation = useMutation({
    mutationFn: ({ actionId, outputs }: { actionId: string; outputs: WorkflowValuesInput }) =>
      completeWorkflowAction({ workflowId: id!, actionId, outputs }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  const invokeMutation = useMutation({
    mutationFn: ({ actionId, invokeAction }: { actionId: string; invokeAction: WorkflowActionIntent }) =>
      invokeWorkflowAction({ workflowId: id!, actionId, invokeAction }),
    onSuccess: onWriteSuccess,
    onError: onWriteError,
  });

  return {
    workflow: query.data,
    isLoading: query.isPending && !!id,
    isError: query.isError,
    // True while a poll or write is in flight — drives a subtle "updating" affordance.
    isFetching: query.isFetching,
    refetch: query.refetch,
    writeValues: valuesMutation.mutate,
    isWritingValues: valuesMutation.isPending,
    completeAction: actionMutation.mutate,
    isCompletingAction: actionMutation.isPending,
    invokeAction: invokeMutation.mutate,
    isInvokingAction: invokeMutation.isPending,
  };
}
